import { useState, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

interface UseDataTableProps {
  initialLimit?: number;
  initialOrderBy?: string;
  initialOrder?: 'asc' | 'desc';
  syncWithUrl?: boolean;
}

export function useDataTable({ 
  initialLimit = 15, 
  initialOrderBy = '', 
  initialOrder = 'asc', 
  syncWithUrl = true 
}: UseDataTableProps = {}) {
  const history = useHistory();
  const location = useLocation();

  // Local state initialized from URL if syncWithUrl is active
  const [page, setPage] = useState<number>(() => {
    if (!syncWithUrl) return 0;
    const pageParam = new URLSearchParams(location.search).get('page');
    return pageParam ? parseInt(pageParam, 10) : 0;
  });
  const [limit, setLimit] = useState(initialLimit);
  const [orderBy, setOrderBy] = useState<string>(() => {
    if (!syncWithUrl) return initialOrderBy;
    return new URLSearchParams(location.search).get('orderBy') || initialOrderBy;
  });
  const [order, setOrder] = useState<'asc' | 'desc'>(() => {
    if (!syncWithUrl) return initialOrder;
    const orderParam = new URLSearchParams(location.search).get('order');
    return orderParam === 'asc' || orderParam === 'desc' ? orderParam : initialOrder;
  });
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    if (!syncWithUrl) return {};
    const qtParam = new URLSearchParams(location.search).get('qt');
    if (qtParam) {
      try {
        return JSON.parse(qtParam);
      } catch (e) {
        return {};
      }
    }
    return {};
  });

  // Sync state when URL changes externally (e.g. browser back/forward)
  // Avoid creating a new object reference if filters haven't changed, preventing double fetches
  useEffect(() => {
    if (syncWithUrl) {
      const params = new URLSearchParams(location.search);
      const pageParam = params.get('page');
      const qtParam = params.get('qt');
      const orderParam = params.get('order');
      const orderByParam = params.get('orderBy');

      const nextPage = pageParam ? parseInt(pageParam, 10) : 0;
      setPage((prev) => (prev === nextPage ? prev : nextPage));

      let nextFilters: Record<string, string> = {};
      if (qtParam) {
        try {
          nextFilters = JSON.parse(qtParam);
        } catch (e) {
          nextFilters = {};
        }
      }
      setFilters((prev) => (JSON.stringify(prev) === JSON.stringify(nextFilters) ? prev : nextFilters));

      if (orderByParam) {
        setOrderBy((prev) => (prev === orderByParam ? prev : orderByParam));
      }
      if (orderParam === 'asc' || orderParam === 'desc') {
        setOrder((prev) => (prev === orderParam ? prev : orderParam));
      }
    }
  }, [syncWithUrl, location.search]);

  // Update URL function
  const updateUrl = useCallback((newPage: number, newFilters: Record<string, string>, newOrderBy: string, newOrder: string) => {
    if (!syncWithUrl) return;
    
    const params = new URLSearchParams(location.search);
    
    // Page
    if (newPage > 0) params.set('page', newPage.toString());
    else params.delete('page');

    // Filters
    if (Object.keys(newFilters).length > 0) {
      params.set('qt', JSON.stringify(newFilters));
    } else {
      params.delete('qt');
    }

    // Sort
    if (newOrderBy) {
       params.set('orderBy', newOrderBy);
       params.set('order', newOrder);
    } else {
       params.delete('orderBy');
       params.delete('order');
    }

    history.push({
      pathname: location.pathname,
      search: `?${params.toString()}`
    });
  }, [history, location.pathname, location.search, syncWithUrl]);


  // Handlers
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    updateUrl(newPage, filters, orderBy, order);
  }, [filters, orderBy, order, updateUrl]);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(0); // Reset page on limit change
    updateUrl(0, filters, orderBy, order);
  }, [filters, orderBy, order, updateUrl]);

  const handleSort = useCallback((columnId: string, newOrder: 'asc' | 'desc') => {
    setOrderBy(columnId);
    setOrder(newOrder);
    setPage(0); // Reset page on sort change
    updateUrl(0, filters, columnId, newOrder);
  }, [filters, updateUrl]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(0); // Reset page on filter change
    updateUrl(0, newFilters, orderBy, order);
  }, [orderBy, order, updateUrl]);

  return {
    page,
    limit,
    orderBy,
    order,
    filters,
    handlePageChange,
    handleLimitChange,
    handleSort,
    handleFilterChange
  };
}
